import { parseAsStringEnum, useQueryState } from 'nuqs';

import { QuizsetCursorSearchReqContentType } from '@/api/gen/_model/quizsetCursorSearchReqContentType';

export type LibraryContentType = QuizsetCursorSearchReqContentType;

/** 사용자가 체크하는 축. 서버 `contentType` 은 이 둘을 접은 결과다. */
export type LibraryContentKind = 'QUIZSET' | 'FOLDER';

export const CONTENT_TYPE_SECTION_LABEL = '자료 유형';

export const CONTENT_TYPE_OPTIONS: {
  value: LibraryContentKind;
  label: string;
}[] = [
  { value: 'QUIZSET', label: '퀴즈' },
  { value: 'FOLDER', label: '폴더' },
];

/**
 * 체크 상태 판정. `ALL` 은 **둘 다 켜진 것**으로 본다 — 자료실 기본값이 전체이고,
 * 시안도 둘 다 선택한 상태를 전체로 설명한다.
 */
export const isContentTypeSelected = (
  contentType: LibraryContentType,
  kind: LibraryContentKind,
) =>
  contentType === QuizsetCursorSearchReqContentType.ALL || contentType === kind;

/** 체크를 토글한 뒤의 서버 값. 둘 다 끄는 상태는 의미가 없어 전체로 되돌린다. */
const nextContentType = (
  contentType: LibraryContentType,
  kind: LibraryContentKind,
): LibraryContentType => {
  const selected = new Set<LibraryContentKind>(
    contentType === QuizsetCursorSearchReqContentType.ALL
      ? ['QUIZSET', 'FOLDER']
      : [contentType as LibraryContentKind],
  );

  if (selected.has(kind)) selected.delete(kind);
  else selected.add(kind);

  if (selected.size !== 1) return QuizsetCursorSearchReqContentType.ALL;
  return [...selected][0];
};

/**
 * 자료실의 자료 유형 필터 (퀴즈 · 폴더 체크박스).
 *
 * 값은 URL 쿼리에 싣는다 — 목록 쿼리 키에 들어가 있어서 필터를 바꾸면 커서가 자동으로
 * 버려지고 새 목록을 받는다. 다른 조건의 커서를 이어 쓰면 서버가 거절한다.
 */
export const useLibraryContentTypeQuery = () => {
  const [contentType, setContentType] = useQueryState(
    'type',
    parseAsStringEnum<LibraryContentType>(
      Object.values(QuizsetCursorSearchReqContentType),
    ).withDefault(QuizsetCursorSearchReqContentType.ALL),
  );

  const write = (next: LibraryContentType) => {
    void setContentType(next, { history: 'replace', shallow: true });
  };

  return {
    contentType,
    toggleContentType: (kind: LibraryContentKind) =>
      write(nextContentType(contentType, kind)),
    resetContentType: () => write(QuizsetCursorSearchReqContentType.ALL),
  };
};
